import React, { useEffect, useState } from "react";
import Managed, { useManaged } from "../common/components/Managed";
import api from "../common/lib/api";
import auth from "../common/lib/auth";
import {
  FormGroup,
  InputGroup,
  TextArea,
  Checkbox,
  Button,
  NumericInput,
  Intent,
  Callout,
} from "@blueprintjs/core";
import numeral from "numeral";
import AsyncButton from "../common/components/AsyncButton";
import { useParams } from "react-router-dom";
import { editDistance } from "./editDistance";

async function loadCompanies(system, setState, setError, setForm) {
  try {
    const { data } = await api.get("/sale-orders/new/" + system);
    const allSaleItems = data.data.saleItems
      .flat(2)
      .filter((x) => x.saleItemId);

    const onlineOrderSalesRep = pickDefaultSalesRep(data);
    setState({ ...data.data, allSaleItems });
    setForm((form) => ({
      ...form,
      companyId: data.data.companies[0].companyId,
      deliveryMethod: data.data.deliveryMethods[0],
      salesRepId: onlineOrderSalesRep.salesRepId,
    }));
  } catch (e) {
    setError(e);
  }
}

function pickDefaultSalesRep(data) {
  const displayName = auth.user && auth.user.displayName;
  let closestRep = null;
  let closestRepDistance = 9e99;
  if (displayName) {
    for (const salesRep of data.data.salesReps) {
      const distance = editDistance(
        salesRep.name.toLowerCase(),
        displayName.toLowerCase()
      );
      if (distance < closestRepDistance) {
        closestRep = salesRep;
        closestRepDistance = distance;
      }
    }

    if (closestRep) {
      return closestRep;
    }
  }
  return (
    data.data.salesReps.find((item) => item.name === "Online Order") ||
    data.data.salesReps[0]
  );
}

async function loadCompany(system, companyId, setState, setError, setForm) {
  try {
    const { data } = await api.get(
      "/sale-orders/new/" + system + "/company/" + companyId
    );
    setState((state) => ({ ...state, ...data.data }));
    setForm((form) => ({
      ...form,
      receiptEmail: data.data.receiptEmail,
      deliveryAddress: data.data.deliveryAddresses[0],
      lines: [],
    }));
  } catch (e) {
    setError(e);
  }
}

async function submitOrder(system, order, onDone, onError) {
  try {
    const { companyId, ...rest } = order;
    const { data } = await api.post(
      `/sale-orders/new/${system}/company/${companyId}/order`,
      rest
    );
    onDone(data.data);
  } catch (e) {
    onError(e.response.data.errors);
  }
}

function SaleItemSelect({ saleItems, value, onChange }) {
  return (
    <div className="bp3-select">
      <select required value={value} onChange={onChange}>
        {saleItems.map(([label, items]) => (
          <optgroup key={label} label={label}>
            {items.map((item) => (
              <option key={item.saleItemId} value={item.saleItemId}>
                {item.name !== item.saleItemId
                  ? `${item.saleItemId} (${item.name})`
                  : item.saleItemId}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </div>
  );
}

const FIELD_LOOKUP = {
  deliveryMethod: "Delivery Method",
  lines: "Products",
  salesRepId: "Salesman",
  companyId: "Company",
  customerOrderNo: "Customer Order No",
  deliveryAddress: "Delivery Address",
  provideFreeShipping: "Free Shipping",
  deliveryNote: "Delivery Note",
  receiptEmail: "Receipt Email",
};

const DEFAULT_FORM = {
  companyId: null,
};

export default function Page() {
  const { system } = useParams();
  const [state, setState, setError] = useManaged();
  const [form, setForm] = useState(DEFAULT_FORM);
  const [formErrors, setFormErrors] = useState([]);

  useEffect(() => {
    loadCompanies(system, setState, setError, setForm);
  }, [setError, setState, setForm, system]);

  useEffect(() => {
    if (form.companyId) {
      loadCompany(system, form.companyId, setState, setError, setForm);
    }
  }, [system, form.companyId, setError, setState]);

  const { data } = state;
  const {
    companies,
    salesReps,
    deliveryMethods,
    saleItems,
    allSaleItems,
    specialPrices,
  } = data || {};

  function formField(field, value = "value") {
    return {
      [value]: form[field] || "",
      onChange: (e) => {
        const v = e.target[value];
        setForm({ ...form, [field]: v });
      },
    };
  }

  async function submitForm() {
    setFormErrors([]);
    await submitOrder(
      system,
      form,
      (order) => {
        window.location = "/sale-order/" + order.saleOrderId;
      },
      setFormErrors
    );
  }

  console.log(JSON.stringify(form, null, 1));

  return (
    <>
      <Managed state={state}>
        {data && (
          <>
            <h1>New Sale Order</h1>
            <form onSubmit={(e) => e.preventDefault()}>
              <FormGroup
                label="Salesman:"
                labelFor="text-input"
                labelInfo="(required)"
                helperText="Who is making the sale?"
              >
                <div className="bp3-select">
                  <select required {...formField("salesRepId")}>
                    {salesReps.map((salesRep) => (
                      <option
                        key={salesRep.salesRepId}
                        value={salesRep.salesRepId}
                      >
                        {salesRep.name}
                      </option>
                    ))}
                  </select>
                </div>
              </FormGroup>

              <FormGroup
                label="Customer:"
                labelFor="text-input"
                labelInfo="(required)"
                helperText="Which customer is this order for?"
              >
                <div className="bp3-select">
                  <select required {...formField("companyId")}>
                    {companies.map((company) => (
                      <option key={company.companyId} value={company.companyId}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>
              </FormGroup>

              <FormGroup
                label="Customer Order No:"
                labelFor="text-input"
                helperText="Only orders for these customers are allowed."
              >
                <InputGroup {...formField("customerOrderNo")} fill={false} />
              </FormGroup>

              <FormGroup
                label="Delivery Address:"
                labelFor="text-input"
                labelInfo="(required)"
                helperText="Where will this order be posted to? Past addresses:"
              >
                <TextArea
                  required
                  {...formField("deliveryAddress")}
                  growVertically
                  rows="5"
                ></TextArea>
              </FormGroup>
              <FormGroup>
                <div className="print-hide">
                  {((data && data.deliveryAddresses) || []).map((address) => (
                    <Button
                      style={{ whiteSpace: "pre" }}
                      minimal
                      onClick={() => {
                        setForm((form) => ({
                          ...form,
                          deliveryAddress: address,
                        }));
                      }}
                    >
                      {address}
                    </Button>
                  ))}
                </div>
              </FormGroup>

              <FormGroup
                label="Delivery Method:"
                labelFor="text-input"
                labelInfo="(required)"
                helperText="Who will deliver to the delivery address?"
              >
                <div className="bp3-select">
                  <select required {...formField("deliveryMethod")}>
                    {deliveryMethods.map((deliveryMethod) => (
                      <option key={deliveryMethod} value={deliveryMethod}>
                        {deliveryMethod}
                      </option>
                    ))}
                  </select>
                </div>
              </FormGroup>
              <Checkbox
                label="Provide Free Shipping"
                {...formField("provideFreeShipping", "checked")}
              />

              <FormGroup
                label="Delivery Note:"
                labelFor="text-input"
                helperText="e.g. Beware of dog"
              >
                <TextArea {...formField("comment")} growVertically></TextArea>
              </FormGroup>

              <FormGroup
                label="Receipt Email:"
                labelFor="text-input"
                helperText="Where to email packing slip?"
              >
                <InputGroup {...formField("receiptEmail")} fill={false} />
              </FormGroup>

              <FormGroup
                label="Order Products:"
                labelFor="text-input"
                labelInfo="(required)"
                helperText="What products are being ordered?"
              >
                <table className="bp3-html-table bp3-html-table-striped bp3-html-table-bordered">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Description</th>
                      <th>Price</th>
                      <th>Quantity</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(!form.lines || form.lines.length === 0) && (
                      <tr>
                        <th colSpan="5">Nothing yet</th>
                      </tr>
                    )}
                    {(form.lines || []).map((line, index) => {
                      const { saleItemId, quantity, unitPrice, description } =
                        line;
                      const saleItem = allSaleItems.find(
                        (x) => x.saleItemId === saleItemId
                      );
                      function change(field) {
                        return (e) => {
                          const value =
                            typeof e === "object" ? e.target.value : e;
                          line[field] = value;

                          if (field === "saleItemId" || field === "quantity") {
                            updateLinePrice(line, allSaleItems, specialPrices);
                          }

                          setForm((form) => ({
                            ...form,
                            lines: form.lines.map((lineItem, lineIndex) =>
                              lineIndex === index
                                ? { ...line }
                                : { ...lineItem }
                            ),
                          }));
                        };
                      }
                      return (
                        <tr>
                          <td>
                            <SaleItemSelect
                              value={saleItemId}
                              saleItems={saleItems}
                              onChange={change("saleItemId")}
                            />
                          </td>
                          <td style={{ whiteSpace: "pre-wrap" }}>
                            <textarea
                              style={{
                                width: "100%",
                                height: "100%",
                                minWidth: "600px",
                                outline: "none",
                              }}
                              value={description}
                              onChange={change("description")}
                            ></textarea>
                          </td>
                          <td>
                            <NumericInput
                              value={unitPrice}
                              onValueChange={change("unitPrice")}
                              leftIcon="dollar"
                              fill
                              min="1"
                            />
                            {unitPrice !== saleItem.unitPrice && (
                              <small>
                                {numeral(
                                  1 - unitPrice / saleItem.unitPrice
                                ).format("0.0%")}{" "}
                                discount from{" "}
                                {numeral(saleItem.unitPrice).format("$0")}
                              </small>
                            )}
                          </td>
                          <td>
                            <NumericInput
                              value={quantity}
                              onValueChange={change("quantity")}
                              leftIcon="cross"
                              fill
                              min="1"
                            />
                          </td>
                          <td>
                            <Button
                              onClick={(e) => {
                                setForm((form) => ({
                                  ...form,
                                  lines: form.lines.filter((x) => x !== line),
                                }));
                              }}
                            >
                              Remove Product
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                    <tr>
                      <td colSpan="5">
                        <Button
                          onClick={(e) => {
                            const firstSaleItem =
                              allSaleItems.find(
                                (x) => x.saleItemId === "PM45 - 4G"
                              ) || allSaleItems[0];
                            setForm((form) => ({
                              ...form,
                              lines: [
                                ...(form.lines || []),
                                updateLinePrice(
                                  {
                                    saleItemId: firstSaleItem.saleItemId,
                                    quantity: 1,
                                    description: firstSaleItem.description,
                                  },
                                  allSaleItems,
                                  specialPrices
                                ),
                              ],
                            }));
                          }}
                        >
                          Add Product
                        </Button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </FormGroup>
              {Boolean(formErrors && formErrors.length) && (
                <Callout intent={Intent.DANGER}>
                  <ol>
                    {formErrors.map((error) => (
                      <li>
                        {`${FIELD_LOOKUP[error.code]} ${error.title}`.trim()}
                      </li>
                    ))}
                  </ol>
                </Callout>
              )}
              <p>
                <AsyncButton
                  intent={Intent.PRIMARY}
                  type="button"
                  onClick={submitForm}
                >
                  Submit Order
                </AsyncButton>
              </p>
            </form>
          </>
        )}
      </Managed>
    </>
  );
}

function updateLinePrice(line, allSaleItems, specialPrices) {
  const saleItem = allSaleItems.find((x) => x.saleItemId === line.saleItemId);
  line.saleItemId = saleItem.saleItemId;
  line.unitPrice = saleItem.unitPrice;
  line.description = saleItem.description;
  for (const specialPrice of specialPrices) {
    if (
      specialPrice.saleItemId === line.saleItemId &&
      specialPrice.minQuantity <= line.quantity // &&
      // Removing this check as per AT-409
      // line.unitPrice > specialPrice.unitPrice
    ) {
      line.unitPrice = specialPrice.unitPrice;
    }
  }
  return line;
}
